import { formatChips, formatChipsShort } from '@polf/shared/locale'

import {
  BeatTheProEntryStateEnum,
  type PolfLeaderboardPlayerEntryFragment,
} from '../../__generated__/hooks'
import DetailSheet from './DetailSheet'
import MiniScorecard, { type CourseHoleSummary } from './MiniScorecard'

/**
 * POLF mode detail sheet. Hero: rank + name + total chips inkl side
 * games as the primary stat, stableford + headline specials inline as
 * secondary. Body: chip-source breakdown card (the legacy "where did
 * those chips come from?" table) + hole-by-hole scorecard.
 */
const PolfDetail = ({
  open,
  entry,
  courseHoles,
  onClose,
}: {
  open: boolean
  entry: PolfLeaderboardPlayerEntryFragment | null
  courseHoles: readonly CourseHoleSummary[]
  onClose: () => void
}) => {
  if (!entry) return null
  const flightNum = entry.player.flight?.flightNumber
  const subtitleParts: string[] = [`${entry.holesRegistred} spilte hull`]
  if (flightNum != null) subtitleParts.push(`flight #${flightNum}`)
  if (entry.player.hcp != null) subtitleParts.push(`hcp ${entry.player.hcp}`)
  const subtitle = subtitleParts.join(' · ')

  // Headline specials — only the ones that actually happened, so the
  // line stays tight on a phone.
  const specials: string[] = []
  if (entry.birdies.count > 0) specials.push(`${entry.birdies.count} 🐦`)
  if (entry.eagles.count > 0) specials.push(`${entry.eagles.count} 🦅`)
  if (entry.holeInOnes.count > 0) specials.push(`${entry.holeInOnes.count} HiO`)
  const secondaryBits: string[] = [`${entry.stablefordPoints.count} pts`]
  if (specials.length > 0) secondaryBits.push(specials.join(' '))
  if (entry.isAllIn) secondaryBits.push('ALL IN')

  return (
    <DetailSheet
      open={open}
      onClose={onClose}
      rank={entry.rank}
      title={entry.player.name}
      subtitle={subtitle}
      primary={
        <>
          {formatChipsShort(entry.chipsInclHoleCompetitions)}
          <span className="text-base font-sans font-normal text-ink-500 ml-1.5">
            chips
          </span>
        </>
      }
      secondary={secondaryBits.join(' · ')}
    >
      <ChipBreakdown entry={entry} />
      <SectionCard label="Scorekort">
        <MiniScorecard
          courseHoles={courseHoles}
          scores={entry.scores}
          strokesAllowance={entry.player.strokes}
        />
      </SectionCard>
    </DetailSheet>
  )
}

type Row = { label: string; count?: number; chips: number }

/** White paper card with a small eyebrow label. Lives on the
 * cream-bg sheet canvas — white-on-cream is the app's high-contrast
 * pattern (same as leaderboard rows on the main page). */
const SectionCard = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <section className="rounded-xl bg-paper shadow-sm overflow-hidden">
    <div className="px-3 pt-3 pb-1 up text-ink-500 text-[10px]">{label}</div>
    {children}
  </section>
)

const ChipBreakdown = ({
  entry,
}: {
  entry: PolfLeaderboardPlayerEntryFragment
}) => {
  // Single unified source list — flatten the legacy chip / sidespill
  // split so the row counts are obvious at a glance. Show only
  // contributing rows; hide zeros to keep the table tight.
  const rows: Row[] = [
    { label: 'Startstack', chips: entry.startStack },
    {
      label: 'Stableford',
      count: entry.stablefordPoints.count,
      chips: entry.stablefordPoints.chips,
    },
    {
      label: 'Birdies',
      count: entry.birdies.count,
      chips: entry.birdies.chips,
    },
    { label: 'Eagles', count: entry.eagles.count, chips: entry.eagles.chips },
    {
      label: 'HiO',
      count: entry.holeInOnes.count,
      chips: entry.holeInOnes.chips,
    },
    {
      label: 'Flight bonus',
      chips: entry.flightBonus ?? 0,
    },
    {
      label: 'Closest to the pin',
      count: entry.closest.count,
      chips: entry.closest.chips,
    },
    {
      label: 'Longest drive',
      count: entry.longest.count,
      chips: entry.longest.chips,
    },
  ].filter((r) => r.chips > 0 || (r.count ?? 0) > 0)

  // Beat the PRO is a signed side bet: WON adds bet×2 (gross — the stake is
  // never escrowed, so the full 2× lands as chips), LOST subtracts bet. It
  // gets its own coloured row and MUST show its negative value, otherwise a
  // lost bet vanishes and the ledger silently stops reconciling.
  const btp = entry.beatTheProExtra
  const btpValue = btp?.value ?? 0
  const btpWon = btp?.state === BeatTheProEntryStateEnum.Won
  const showBtp = btp != null && btpValue !== 0
  const btpSub = btp?.allIn
    ? `All-in · ${btpWon ? 'vunnet' : 'tapt'}`
    : `Innsats ${formatChips(btp?.bet ?? 0)} · ${btpWon ? 'vunnet' : 'tapt'}`

  return (
    <SectionCard label="Oppsummering">
      <ul className="px-3 pb-2">
        {rows.map((r) => (
          <li
            key={r.label}
            className="grid grid-cols-[1fr_2.75rem_4.5rem] items-baseline gap-2 py-1 text-sm"
          >
            <span className="text-ink-700 truncate">{r.label}</span>
            <span className="font-mono num text-ink-400 text-right">
              {r.count != null && r.count > 0 ? `${r.count} ×` : ''}
            </span>
            <span className="font-mono num text-ink-900 text-right">
              {formatChips(r.chips)}
            </span>
          </li>
        ))}
        {showBtp && (
          <li className="grid grid-cols-[1fr_2.75rem_4.5rem] items-baseline gap-2 py-1 text-sm">
            <span className="min-w-0">
              <span className="block text-ink-700 truncate">Beat the PRO</span>
              <span className="block text-[11px] text-ink-500 truncate">
                {btpSub}
              </span>
            </span>
            <span aria-hidden />
            <span
              className={`font-mono num text-right font-semibold ${
                btpWon ? 'text-gold' : 'text-flag'
              }`}
            >
              {btpValue > 0
                ? `+${formatChips(btpValue)}`
                : formatChips(btpValue)}
            </span>
          </li>
        )}
        <li className="grid grid-cols-[1fr_4.5rem] items-baseline gap-2 py-1.5 mt-1 border-t border-ink-200">
          <span className="up text-ink-500 text-[10px]">Totalt</span>
          <span className="font-mono num font-bold text-base text-ink-900 text-right leading-none">
            {formatChips(entry.chipsInclHoleCompetitions)}
          </span>
        </li>
      </ul>
      {showBtp && btpWon && (
        <p className="px-3 pb-3 text-[11px] leading-snug text-ink-500">
          Beat the PRO vises brutto: gevinsten inkluderer innsatsen tilbake.
        </p>
      )}
    </SectionCard>
  )
}

export default PolfDetail
